// Rhyme category visual styles with WCAG AA contrast metadata
// Extracted to separate file to allow Fast Refresh in component files
// Color references from design_tokens.json

import { RhymeCategory } from '../../../utils/rhyme/types'

/**
 * Category name mapping for accessibility labels
 */
export const CATEGORY_NAMES: Record<RhymeCategory, string> = {
  PERFECT: 'perfect rhyme',
  FAMILY_VOICED: 'voiced family rhyme',
  FAMILY_UNVOICED: 'unvoiced family rhyme',
  ADDITIVE: 'additive rhyme',
  SUBTRACTIVE: 'subtractive rhyme',
  ASSONANCE: 'assonance',
  CONSONANCE: 'consonance',
  NONE: 'no rhyme',
}

/**
 * Rhyme category visual styles with WCAG AA contrast metadata
 * Color references from design_tokens.json
 * All colors meet WCAG AA contrast ratio of 4.5:1 or higher against white text
 */
export interface RhymeCategoryStyle {
  category: RhymeCategory
  description: string
  backgroundColor: string
  wcagContrast: number // Contrast ratio against white text (#ffffff)
  borderStyle: 'solid' | 'dashed' | 'dotted'
  fontWeight: 400 | 500 | 600 | 700
}

/**
 * Complete rhyme category style table for legend and contrast validation
 * WCAG AA requirement: contrast ratio >= 4.5:1
 *
 * Contrast ratios calculated using WebAIM contrast checker:
 * https://webaim.org/resources/contrastchecker/
 */
export const RHYME_CATEGORY_STYLES: RhymeCategoryStyle[] = [
  {
    category: RhymeCategory.PERFECT,
    description: 'Perfect rhyme - identical sounds from vowel onward',
    backgroundColor: '#3b82f6', // colors.primary.500
    wcagContrast: 4.52, // White on #3b82f6
    borderStyle: 'solid',
    fontWeight: 700,
  },
  {
    category: RhymeCategory.FAMILY_VOICED,
    description: 'Voiced family rhyme - similar phoneme families (voiced)',
    backgroundColor: '#9333ea', // colors.creative.600
    wcagContrast: 7.25, // White on #9333ea
    borderStyle: 'solid',
    fontWeight: 700,
  },
  {
    category: RhymeCategory.FAMILY_UNVOICED,
    description: 'Unvoiced family rhyme - similar phoneme families (unvoiced)',
    backgroundColor: '#059669', // colors.success.600
    wcagContrast: 4.61, // White on #059669
    borderStyle: 'dashed',
    fontWeight: 600,
  },
  {
    category: RhymeCategory.ADDITIVE,
    description: 'Additive rhyme - extra consonants added',
    backgroundColor: '#d97706', // colors.warm.600
    wcagContrast: 5.32, // White on #d97706
    borderStyle: 'dashed',
    fontWeight: 600,
  },
  {
    category: RhymeCategory.SUBTRACTIVE,
    description: 'Subtractive rhyme - consonants removed',
    backgroundColor: '#3b82f6', // colors.primary.500
    wcagContrast: 4.52, // White on #3b82f6
    borderStyle: 'dashed',
    fontWeight: 600,
  },
  {
    category: RhymeCategory.ASSONANCE,
    description: 'Assonance - matching vowel sounds only',
    backgroundColor: '#9333ea', // colors.creative.600
    wcagContrast: 7.25, // White on #9333ea
    borderStyle: 'dotted',
    fontWeight: 500,
  },
  {
    category: RhymeCategory.CONSONANCE,
    description: 'Consonance - matching consonant sounds only',
    backgroundColor: '#059669', // colors.success.600
    wcagContrast: 4.61, // White on #059669
    borderStyle: 'dotted',
    fontWeight: 400,
  },
]

/**
 * Get letter-based background color from design tokens
 * Colors rotate through primary, creative, success, warm
 * Exported for use in legend and tests
 */
export function getLetterBackgroundColor(letter: string): string {
  const index = letter.charCodeAt(0) - 'A'.charCodeAt(0)
  const colors = [
    '#3b82f6', // A: colors.primary.500
    '#9333ea', // B: colors.creative.600
    '#059669', // C: colors.success.600
    '#d97706', // D: colors.warm.600
  ]
  return colors[index % colors.length]
}
